import classNames from "classnames";
import { useContext, useEffect, useMemo, useState } from "react";
import { AvisUtils, ErrorUtils, IAxiosError, api } from "../../../utils";
import { isMobileView } from "../appStates/appStates";
import { AvisLoader } from "../components";
import { AvisPrimaryButton } from "../components/buttons";
import { AvisContext } from "../context/AvisContext";

interface IErrorProps {
  className?: string;
  title?: string;
  content?: string;
  info: string | undefined;
  stackTrace?: string;
  showToHomePageButton?: boolean;
  showLogoutButton?: boolean;
  axiosError?: IAxiosError;
}
interface ILogRow {
  label: string | undefined;
  value: string | boolean | undefined;
}
export function ErrorPage(props: Readonly<IErrorProps>): React.JSX.Element {
  const {
    className,
    content,
    info,
    title,
    stackTrace,
    axiosError,
    showToHomePageButton,
    showLogoutButton,
  } = props;
  const { res, logout } = useContext(AvisContext);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [logId, setLogId] = useState<string>();
  const [redirecting, setRedirecting] = useState<boolean>(false);

  useEffect(() => {
    //Log only once
    if (loaded || logId || !info) return;

    const paramList: ILogRow[] = [
      { label: title, value: info },
      { label: "Location", value: window.location.href },
      { label: "IsMobileView", value: isMobileView.value },
      { label: "UserAgent", value: navigator.userAgent },
      {
        label: "Dimensions",
        value: `W:${window.innerWidth}, H:${window.innerHeight}`,
      },
      { label: "Info", value: stackTrace },
    ];

    const fullErrorData = paramList
      .map((lrow) => `${lrow.label}: ${lrow.value}`)
      .join("\n");
    api
      .logPostErrorData({
        errorData: fullErrorData,
      })
      .then((response) => {
        setLogId(response.data);
      });
  }, [title, info, loaded, logId, stackTrace]);

  useEffect(() => {
    const redirectOnStartPageOnErrors = [
      "InvalidCharacterError: Failed to execute 'atob' on 'Window'",
      "InvalidCharacterError: String contains an invalid character",
    ];
    if (info && typeof info === "string" && !redirecting) {
      const navigateToStart =
        redirectOnStartPageOnErrors.filter(
          (err) => info.toLowerCase().indexOf(err.toLowerCase()) > -1,
        ).length > 0;

      if (navigateToStart) {
        location.href = AvisUtils.getStartPagePath();
        setRedirecting(true);
        setLoaded(false);
        return;
      }
    }
    setLoaded(!redirecting);
  }, [title, info, loaded, redirecting]);
  const componentClassName = classNames(className, "avis-error-page");
  const titleRes = res("error_Title");
  const buttonRes = res("button_ToHomePage");
  const errorIdRes = res("error_ErrorCode", logId ?? "");

  const getText = (optText: string, resText?: string): string => {
    // in case the resources didn't load
    if (!resText || resText === "") return optText;

    return resText;
  };

  const getInfo = () => {
    if (axiosError) return ErrorUtils.getGeneralErrorMessage(axiosError, res);

    return info;
  };

  const renderErrorId = () => {
    if (!logId) return;

    return (
      <div className="avis-error-id">
        {/* do not localize this */}
        {getText("Kļūdas kods: " + logId, errorIdRes)}
      </div>
    );
  };
  const renderInfo = () => {
    if (!info && !axiosError) return;

    return <div className="avis-error-info">{getInfo()}</div>;
  };

  const renderTitle = () => {
    return (
      <div className="avis-error-title">
        <div>
          <i className="fas fa-exclamation-triangle" />
          {/* do not localize this */}
          <span className="pl-2">{title ?? getText("Kļūda", titleRes)}</span>
        </div>
      </div>
    );
  };

  const renderContent = useMemo(() => {
    if (!content) return;

    return <div className="avis-error-content">{content}</div>;
  }, [content]);

  const renderHomeButton = () => {
    if (!showToHomePageButton) return;

    return (
      <AvisPrimaryButton
        onClick={() => location.assign(AvisUtils.getStartPagePath())}
        /* do not localize this */
        label={getText("Uz sākumu", buttonRes)}
      />
    );
  };
  const renderLogoutButton = () => {
    if (!showLogoutButton) return;

    return (
      <AvisPrimaryButton
        onClick={logout}
        /* do not localize this */
        label={getText("Iziet", res("button_Exit"))}
      />
    );
  };

  if (!loaded) return <AvisLoader />;
  return (
    <div className={componentClassName}>
      {renderTitle()}
      {renderInfo()}
      {renderContent}
      {renderErrorId()}
      {renderHomeButton()}
      {renderLogoutButton()}
    </div>
  );
}
